/**
 * Route + navigation config — shared nav infrastructure for admin-dashboard and
 * cleaning-app. Rewritten for the ServiceVault cleaning domain (was IoT).
 *
 * Roles use the lowercase ServiceVault set via the back-compat `UserRole` enum
 * (admin > manager > worker/customer). `admin` sees everything.
 */
import { UserRole } from '../types/auth';
import type { Feature } from '../subscription/plans';
import dashboardSections from './dashboard-sections.json';

export type LayoutType = 'default' | 'admin' | 'auth' | 'public' | 'user';
export type RouteType = 'page' | 'app' | 'auth' | 'admin' | 'protected';

export type RouteConfig = {
  key: string;
  path: string;
  label: string;
  type: RouteType;
  layout?: LayoutType;
  roles?: UserRole[];
  public?: boolean;
  category?: string;
  /** Permission-matrix module(s) this page belongs to (see IdentityService PermissionsController.Matrix). The page is shown when the person's
   * role may use it there: the module's View cell if it has one, otherwise any of its cells. */
  modules?: string[];
  icon?: string;
  hideInSidebar?: boolean;
  subItems?: RouteConfig[];
  /**
   * Subscription feature key required to see/use this route, on top of the role check above.
   * Only tagged where a real feature genuinely maps to a real module (see FEATURE_MIN_PLAN's
   * doc history) — several catalog feature keys (tenant.sso, cms.multi_brand,
   * marketplace.contractors, ai.auto_reschedule, ai.dunning) have no corresponding admin
   * module in this app and are deliberately left untagged rather than force-mapped to
   * something unrelated just to "use" the key. `admin` bypasses this the same as role checks.
   */
  feature?: Feature;
};

export type RouteKey =
  | 'LANDING'
  | 'SIGNIN'
  | 'SIGNUP'
  | 'FORGOT_PASSWORD'
  | 'DASHBOARD'
  | 'ADMIN_DASHBOARD'
  | 'BOOKINGS'
  | 'ORDERS'
  | 'QUOTES'
  | 'PACKAGES'
  | 'AREAS'
  | 'ITEMS'
  | 'SCHEDULE'
  | 'WORKERS'
  | 'CRM'
  | 'CONTACTS'
  | 'MESSAGES'
  | 'NOTIFICATIONS'
  | 'PRODUCTS'
  | 'PRODUCT_CATEGORIES'
  | 'INVENTORY'
  | 'HERO_SLIDES'
  | 'PROMO_TILES'
  | 'PRODUCT_CAMPAIGNS'
  | 'LOYALTY'
  | 'REFERRALS'
  | 'REVIEWS'
  | 'NEWSLETTER'
  | 'SITE'
  | 'CAROUSEL'
  | 'TESTIMONIALS'
  | 'BLOG'
  | 'IMAGES'
  | 'DISCOUNTS'
  | 'LOCATIONS'
  | 'BILLING'
  | 'PAYMENTS'
  | 'CAMPAIGNS'
  | 'CMS'
  | 'USERS'
  | 'ANALYTICS'
  | 'ECOMMERCE_ANALYTICS'
  | 'LIVE_WORKFORCE'
  | 'REPORTS'
  | 'ADMIN_REPORTS'
  | 'AI_OPS'
  | 'SYSTEM_HEALTH'
  | 'AUDIT_LOG'
  | 'PERMISSIONS'
  | 'SETTINGS'
  | 'PROFILE'
  | 'FORBIDDEN'
  | 'PLATFORM_STORES';

const r = (config: RouteConfig): RouteConfig => config;

export const ROUTES: Record<RouteKey, RouteConfig> = {
  LANDING: r({ key: 'LANDING', path: '/landing', label: 'Home', type: 'page', public: true, layout: 'public' }),
  SIGNIN: r({ key: 'SIGNIN', path: '/signin', label: 'Sign In', type: 'auth', public: true, layout: 'auth' }),
  SIGNUP: r({ key: 'SIGNUP', path: '/signup', label: 'Sign Up', type: 'auth', public: true, layout: 'auth' }),
  FORGOT_PASSWORD: r({ key: 'FORGOT_PASSWORD', path: '/forgot-password', label: 'Reset Password', type: 'auth', public: true, layout: 'auth' }),

  DASHBOARD: r({ key: 'DASHBOARD', path: '/dashboard', label: 'Dashboard', type: 'protected', layout: 'user', category: 'Overview', icon: 'LayoutGrid' }),
  // (admin) is a pathless TanStack Router group — real paths below have no '/admin' prefix
  // (verified against the generated routeTree.gen.ts). ADMIN_DASHBOARD is the group's index route.
  ADMIN_DASHBOARD: r({ key: 'ADMIN_DASHBOARD', path: '/', label: 'Dashboard', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN, UserRole.MANAGER], category: 'Overview', modules: ['Dashboard'], icon: 'LayoutGrid' }),
  // Any authenticated role can land here — deliberately listing all four rather than leaving
  // `roles` unset: the (admin) layout's beforeLoad guard treats a route with no roles array as
  // admin-only (the opposite of getSidebarRoutes' roleMatches(), which treats it as "everyone" —
  // a pre-existing inconsistency between the two; naming every role here sidesteps it either way).
  FORBIDDEN: r({ key: 'FORBIDDEN', path: '/403', label: 'Access Denied', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN, UserRole.MANAGER, UserRole.WORKER, UserRole.CUSTOMER], hideInSidebar: true }),

  BOOKINGS: r({ key: 'BOOKINGS', path: '/bookings', label: 'Bookings', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN, UserRole.MANAGER], category: 'Cleaning', icon: 'CalendarCheck', feature: 'booking.online' }),
  // Ecommerce Module 14 — OrderService has no admin-wide "all orders" list endpoint today
  // (GET /api/v1/orders is hard-scoped to the caller's own CustomerId), so the list page is a
  // real UI wired to whatever that endpoint actually returns for the signed-in account, plus
  // an id-lookup jump (GetById/History do allow manager/admin to load any order).
  ORDERS: r({ key: 'ORDERS', path: '/orders', label: 'Orders', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN, UserRole.MANAGER], category: 'Sales & Support', modules: ['Orders'], icon: 'ShoppingCart' }),
  QUOTES: r({ key: 'QUOTES', path: '/quotes', label: 'Quotes', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN, UserRole.MANAGER], category: 'Cleaning', icon: 'FileText', feature: 'booking.online' }),
  PACKAGES: r({ key: 'PACKAGES', path: '/packages', label: 'Packages', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN, UserRole.MANAGER], category: 'Cleaning', icon: 'Package', feature: 'booking.online' }),
  AREAS: r({ key: 'AREAS', path: '/areas', label: 'Service Areas', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN, UserRole.MANAGER], category: 'Cleaning', icon: 'MapPin', feature: 'booking.online' }),
  ITEMS: r({ key: 'ITEMS', path: '/items', label: 'Cleaning Items', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN, UserRole.MANAGER], category: 'Cleaning', icon: 'Sparkles', feature: 'booking.online' }),
  SCHEDULE: r({ key: 'SCHEDULE', path: '/schedule', label: 'Dispatch', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN, UserRole.MANAGER], category: 'Cleaning', icon: 'CalendarClock', feature: 'scheduling.optimizer' }),
  WORKERS: r({ key: 'WORKERS', path: '/workers', label: 'Workers', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN, UserRole.MANAGER], category: 'Cleaning', icon: 'Users', feature: 'workers.geofence' }),
  CRM: r({ key: 'CRM', path: '/crm', label: 'Customers', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN, UserRole.MANAGER], category: 'Cleaning', icon: 'Contact' }),
  CONTACTS: r({ key: 'CONTACTS', path: '/contacts', label: 'Contact Requests', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN, UserRole.MANAGER], category: 'Sales & Support', modules: ['ContactRequests'], icon: 'Mail' }),
  // Module 14 — CrmService's MessagesController/CrmChatHub inbox. A manager/admin sees
  // every customer conversation here (unlike a customer, who only ever has their own).
  MESSAGES: r({ key: 'MESSAGES', path: '/messages', label: 'Messages', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN, UserRole.MANAGER], category: 'Sales & Support', modules: ['Messages'], icon: 'MessageSquare' }),
  // NotificationService's real template management + send log (order-confirmation/
  // order-shipped/abandoned-cart email templates, test-send, admin-wide send log).
  NOTIFICATIONS: r({ key: 'NOTIFICATIONS', path: '/notifications', label: 'Notifications', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN, UserRole.MANAGER], category: 'Sales & Support', modules: ['Notifications'], icon: 'Bell' }),

  // Ecommerce (ProductService) — manager+ writes, same split as the rest of catalog admin.
  PRODUCTS: r({ key: 'PRODUCTS', path: '/products', label: 'Products', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN, UserRole.MANAGER], category: 'Catalog', modules: ['Products'], icon: 'ShoppingBag' }),
  PRODUCT_CATEGORIES: r({ key: 'PRODUCT_CATEGORIES', path: '/products/categories', label: 'Categories', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN, UserRole.MANAGER], category: 'Catalog', modules: ['Categories'], icon: 'FolderTree' }),
  INVENTORY: r({ key: 'INVENTORY', path: '/inventory', label: 'Inventory', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN, UserRole.MANAGER], category: 'Catalog', modules: ['Inventory'], icon: 'Boxes' }),
  HERO_SLIDES: r({ key: 'HERO_SLIDES', path: '/hero-slides', label: 'Hero Slides', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN, UserRole.MANAGER], category: 'Marketing', modules: ['HeroSlides'], icon: 'GalleryHorizontal' }),
  PROMO_TILES: r({ key: 'PROMO_TILES', path: '/promo-tiles', label: 'Promo Tiles', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN, UserRole.MANAGER], category: 'Marketing', modules: ['PromoTiles'], icon: 'LayoutGrid' }),
  PRODUCT_CAMPAIGNS: r({ key: 'PRODUCT_CAMPAIGNS', path: '/product-campaigns', label: 'Campaigns & Offers', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN, UserRole.MANAGER], category: 'Marketing', modules: ['Campaigns'], icon: 'Megaphone', feature: 'campaigns.promos' }),
  DISCOUNTS: r({ key: 'DISCOUNTS', path: '/discounts', label: 'Discount Rules', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN, UserRole.MANAGER], category: 'Marketing', modules: ['Discounts'], icon: 'Percent', feature: 'campaigns.promos' }),
  LOCATIONS: r({ key: 'LOCATIONS', path: '/locations', label: 'Locations & Shipping', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN, UserRole.MANAGER], category: 'Store Setup', modules: ['Locations'], icon: 'MapPin' }),
  LOYALTY: r({ key: 'LOYALTY', path: '/loyalty', label: 'Loyalty Program', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN, UserRole.MANAGER], category: 'Marketing', modules: ['LoyaltyTiers', 'LoyaltyMembers'], icon: 'Star' }),
  REFERRALS: r({ key: 'REFERRALS', path: '/referrals', label: 'Referrals', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN, UserRole.MANAGER], category: 'Marketing', modules: ['Referrals'], icon: 'Share2' }),
  REVIEWS: r({ key: 'REVIEWS', path: '/reviews', label: 'Reviews', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN, UserRole.MANAGER], category: 'Marketing', modules: ['ReviewModeration'], icon: 'MessageSquareText' }),
  // TenantService/NewsletterController — real subscribe/unsubscribe/list, previously wired to
  // no frontend anywhere (storefront or admin).
  NEWSLETTER: r({ key: 'NEWSLETTER', path: '/newsletter', label: 'Newsletter', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN, UserRole.MANAGER], category: 'Marketing', modules: ['Newsletter'], icon: 'Mail' }),

  BILLING: r({ key: 'BILLING', path: '/billing', label: 'Billing', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN, UserRole.MANAGER], category: 'Finance', modules: ['Billing'], icon: 'Receipt' }),
  PAYMENTS: r({ key: 'PAYMENTS', path: '/payments', label: 'Payments', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN, UserRole.MANAGER], category: 'Finance', modules: ['Payments'], icon: 'CreditCard', feature: 'billing.invoicing' }),
  // Booking-business promo campaigns — distinct from PRODUCT_CAMPAIGNS above (ecommerce's
  // own "Campaigns & Offers"). Stays in the Cleaning dashboard (see dashboard-sections.json).
  CAMPAIGNS: r({ key: 'CAMPAIGNS', path: '/campaigns', label: 'Campaigns', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN, UserRole.MANAGER], category: 'Cleaning', icon: 'Megaphone', feature: 'campaigns.promos' }),
  // Website content management — general-purpose, not cleaning-specific, and now the source
  // ecommerce-app's storefront actually reads (see siteApi.getPublic/blogApi/testimonialsApi) —
  // grouped under Ecommerce (see dashboard-sections.json for the enable/disable toggle).
  CMS: r({ key: 'CMS', path: '/cms', label: 'Website (CMS)', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN, UserRole.MANAGER], category: 'Website', modules: ['WebsiteCms'], icon: 'LayoutTemplate' }),
  SITE: r({ key: 'SITE', path: '/site', label: 'Site Settings', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN, UserRole.MANAGER], category: 'Website', modules: ['SiteSettings'], icon: 'Globe' }),
  CAROUSEL: r({ key: 'CAROUSEL', path: '/carousel', label: 'Carousel', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN], category: 'Website', icon: 'GalleryHorizontal' }),
  TESTIMONIALS: r({ key: 'TESTIMONIALS', path: '/testimonials', label: 'Testimonials', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN, UserRole.MANAGER], category: 'Website', modules: ['Testimonials'], icon: 'Quote' }),
  BLOG: r({ key: 'BLOG', path: '/blog', label: 'Blog', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN, UserRole.MANAGER], category: 'Website', modules: ['Blog'], icon: 'Newspaper' }),
  IMAGES: r({ key: 'IMAGES', path: '/image-library', label: 'Image Library', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN], category: 'Website', icon: 'Image' }),

  USERS: r({ key: 'USERS', path: '/users', label: 'Users', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN], category: 'Administration', icon: 'UserCog' }),
  // Real, admin-editable permission-restriction matrix — see IdentityService's
  // PermissionRule/PermissionsController and ApiGateway's PermissionMatrixCache. Admin only,
  // matching the backend's [RequireRoles("admin")] gate on this controller.
  PERMISSIONS: r({ key: 'PERMISSIONS', path: '/permissions', label: 'Permissions', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN], category: 'Administration', icon: 'ShieldCheck' }),
  // AnalyticsService is booking/cleaning domain only (job/worker/quote events, suburb demand) —
  // not ecommerce data, see ECOMMERCE_ANALYTICS below for that. Stays in the Cleaning dashboard.
  ANALYTICS: r({ key: 'ANALYTICS', path: '/analytics', label: 'Analytics', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN, UserRole.MANAGER], category: 'Cleaning', icon: 'BarChart3', feature: 'analytics.dashboards' }),
  // ECOMMERCE_ANALYTICS is deliberately untagged — it's this app's real dashboard '/' route
  // (ADMIN_DASHBOARD renders it), so it can never be feature-gated without locking every
  // tenant out of their own landing page.
  ECOMMERCE_ANALYTICS: r({ key: 'ECOMMERCE_ANALYTICS', path: '/analytics/ecommerce', label: 'Ecommerce Analytics', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN, UserRole.MANAGER], category: 'Overview', modules: ['EcommerceAnalytics'], icon: 'TrendingUp' }),
  LIVE_WORKFORCE: r({ key: 'LIVE_WORKFORCE', path: '/live-workforce', label: 'Live Workforce', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN, UserRole.MANAGER], category: 'Cleaning', icon: 'Radar', feature: 'workers.geofence' }),
  REPORTS: r({ key: 'REPORTS', path: '/reports', label: 'Reports', type: 'protected', layout: 'user', category: 'Insights', icon: 'FileBarChart' }),
  // Real BillingService ReportsController — GST/Campaign Performance/Discount Usage PDFs,
  // async-generated via TickerQ, plus emailed scheduled reports. Moved into the Ecommerce
  // dashboard section (dashboard-sections.json) since it's an ecommerce reporting feature, not
  // the unbuilt "legacyOps" placeholder this route key started as.
  ADMIN_REPORTS: r({ key: 'ADMIN_REPORTS', path: '/admin-reports', label: 'Exports & Reports', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN], category: 'Insights', icon: 'FileBarChart', feature: 'billing.accounting_export' }),
  AI_OPS: r({ key: 'AI_OPS', path: '/ai-ops', label: 'AI Ops', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN], category: 'Insights', icon: 'Bot', feature: 'ai.chat' }),
  // Real gateway /registry (YARP cluster/destination health) + OrderService DLQ backlog —
  // distinct from AI_OPS above, which is a separate (currently non-functional) AI-agent
  // integration, not service health monitoring.
  SYSTEM_HEALTH: r({ key: 'SYSTEM_HEALTH', path: '/system-health', label: 'System Health', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN], category: 'Insights', icon: 'HeartPulse' }),
  // Cross-order audit log (OrderHistory across every order) — see OrdersController.AllHistory.
  // The only real, general-purpose audit trail in the ecommerce line; Product/Cart/Identity/
  // Tenant have no audit entity to show here.
  AUDIT_LOG: r({ key: 'AUDIT_LOG', path: '/audit-log', label: 'Audit Log', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN], category: 'Insights', icon: 'ScrollText', feature: 'tenant.audit_log' }),

  // Platform admin only (a real, separate claim — is_platform_admin — not the UserRole enum,
  // which has no PLATFORM_ADMIN member; see AuthUser.isPlatformAdmin). Registered here — even
  // though it's not in this enum's role list and AppSidebar.tsx renders its own nav link for it
  // rather than going through getGroupedSidebarRoutes — purely so getRouteConfig(pathname)
  // resolves an *exact* match for '/platform/stores' instead of falling through to its
  // longest-prefix fallback, which would otherwise match ADMIN_DASHBOARD (path '/' — a prefix
  // of every path) and gate it as admin/manager-only in the (admin) layout's beforeLoad. Real
  // enforcement is the route's own beforeLoad guard (checks isPlatformAdmin directly), not the
  // `roles` list here — live-caught: without this entry, a genuine platform admin hit /403
  // before ever reaching that guard.
  PLATFORM_STORES: r({ key: 'PLATFORM_STORES', path: '/platform/stores', label: 'Stores', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN], hideInSidebar: true }),
  SETTINGS: r({ key: 'SETTINGS', path: '/settings', label: 'Settings', type: 'admin', layout: 'admin', roles: [UserRole.ADMIN], category: 'Administration', icon: 'Settings' }),
  PROFILE: r({ key: 'PROFILE', path: '/profile', label: 'Profile', type: 'protected', layout: 'user', hideInSidebar: true, icon: 'UserCircle' }),
};

// ── helpers (signatures preserved from the previous config so consumers compile) ──

export const ADMIN_ENTRY_PATH = ROUTES.ADMIN_DASHBOARD.path;
export const USER_ENTRY_PATH = ROUTES.DASHBOARD.path;

const ALL = Object.values(ROUTES);

export const getAllRoutes = (): RouteConfig[] => ALL;

export const getRouteByPath = (path: string): RouteConfig | undefined =>
  ALL.find((route) => route.path === path);

export function getRouteConfig(pathname: string): RouteConfig | undefined {
  // exact match first, then longest-prefix (so /admin/bookings/123 resolves to BOOKINGS)
  return (
    ALL.find((route) => route.path === pathname) ??
    ALL.filter((route) => pathname.startsWith(route.path))
      .sort((a, b) => b.path.length - a.path.length)[0]
  );
}

export function getRoute(key: RouteKey): RouteConfig {
  return ROUTES[key];
}

/**
 * Router-agnostic nav-highlighting predicate — matches AppSidebar's inline `isActive`
 * exactly: root path ('/') requires an exact match, every other path matches on
 * exact-match-or-`startsWith(href + '/')` (so a parent route stays highlighted while
 * on any of its child routes).
 */
export const isRouteActive = (pathname: string, href: string): boolean => {
  if (href === '/') return pathname === '/';
  return pathname === href || pathname.startsWith(`${href}/`);
};

export const isPublicRoute = (path: string): boolean => !!getRouteConfig(path)?.public;
export const isAuthRoute = (path: string): boolean => getRouteConfig(path)?.type === 'auth';
export function isAdminRoute(pathname: string): boolean {
  return getRouteConfig(pathname)?.type === 'admin';
}
export function isProtectedRoute(pathname: string): boolean {
  const config = getRouteConfig(pathname);
  return !!config && !config.public && config.type !== 'auth';
}

/**
 * Does the tenant's real effective feature set (TenantSubscription.EffectiveFeatures) unlock
 * this route? Untagged routes (most of them — see RouteConfig.feature's doc comment) always
 * pass. `admin` bypasses this exactly like roleMatches() bypasses the role check — a platform
 * admin isn't limited by any one tenant's subscription plan.
 */
export function featureMatches(route: RouteConfig, features: string[], role?: UserRole | string): boolean {
  if (!route.feature) return true;
  if (role === UserRole.ADMIN) return true;
  return features.includes(route.feature);
}

export interface PermissionCellLite { module: string; action: string; allowed: boolean }

const BUILT_IN_ROLES = ['admin', 'manager', 'worker', 'customer'];

/** May this person use the page according to the permission matrix? Built-in roles: unless the page's module was switched off for them.
 * `cells` undefined = not loaded yet -> don't hide anything (avoids the menu flashing empty). Admin always may. */
export function matrixAllowsRoute(route: RouteConfig, cells: PermissionCellLite[] | undefined, role?: string): boolean {
  if (role === 'admin' || !route.modules?.length || !cells) return true;
  return route.modules.some((m) => {
    const mc = cells.filter((c) => c.module === m);
    if (mc.length === 0) return true; // module unknown to the matrix -> nothing restricts it
    const view = mc.find((c) => c.action === 'View');
    return view ? view.allowed : mc.some((c) => c.allowed);
  });
}

/** A CUSTOM role has no built-in page access at all; it sees a page only when the matrix explicitly grants it one of the page's modules. */
export function matrixGrantsRoute(route: RouteConfig, cells: PermissionCellLite[] | undefined): boolean {
  if (!route.modules?.length || !cells) return false;
  return route.modules.some((m) => cells.some((c) => c.module === m && c.allowed));
}

/** Sidebar/page visibility = role list (built-in roles) narrowed by the matrix, or a matrix grant (custom roles). */
export function routeVisibleFor(route: RouteConfig, role: string | undefined, cells?: PermissionCellLite[]): boolean {
  if (role && !BUILT_IN_ROLES.includes(role)) return matrixGrantsRoute(route, cells);
  return roleMatches(route, role) && matrixAllowsRoute(route, cells, role);
}

function roleMatches(route: RouteConfig, role?: UserRole | string): boolean {
  if (!route.roles?.length) return true;
  if (role === UserRole.ADMIN) return true; // admin sees everything
  return !!role && route.roles.some((x) => x === role);
}

/**
 * The one role a person is treated as everywhere (sidebar, route guard). Built-in roles rank admin > manager > worker > customer; a custom
 * role only counts when the person has no built-in one. Replaces "roles[0]", which made the sidebar and the guard disagree for anyone who
 * held two roles.
 */
export function primaryRole(roles?: readonly string[] | null): string | undefined {
  if (!roles?.length) return undefined;
  for (const r of ['admin', 'manager', 'worker', 'customer']) if (roles.includes(r)) return r;
  return roles[0];
}

export function canAccessRoute(routeKey: RouteKey, userRole?: UserRole | string): boolean {
  return roleMatches(ROUTES[routeKey], userRole);
}

export const canAccessPath = (path: string, userRole?: UserRole): boolean => {
  const config = getRouteConfig(path);
  if (!config) return false;
  if (config.public) return true;
  return roleMatches(config, userRole);
};

export const getEntryPath = (role?: UserRole | string): string => {
  // platform_admin isn't in the UserRole enum (a real, separate claim — see AuthUser
  // .isPlatformAdmin/JwtTokenService.IssuePairAsync, not a role this shared role-matching
  // system otherwise knows about) and has no tenant-scoped data of its own, so
  // ADMIN_DASHBOARD ('/' — this app's ecommerce analytics landing page) would render empty/
  // meaningless for it. Route straight to its own real landing page instead — live-caught:
  // without this, a platform admin logging in landed on '/dashboard' (USER_ENTRY_PATH, the
  // customer-tier layout with no sidebar at all) via the same fallback a customer role hits.
  if (role === 'platform_admin') return '/platform/stores';
  if (role === UserRole.ADMIN || role === UserRole.MANAGER) return ADMIN_ENTRY_PATH;
  return USER_ENTRY_PATH;
};

/**
 * Ecommerce vs Cleaning dashboard toggle — see dashboard-sections.json. A route not listed
 * there (Dashboard, Users, Settings, Reports, AI Ops, …) is cross-cutting and always shown,
 * regardless of which business-line dashboards are enabled. Edit that JSON file's
 * `dashboards.<name>.enabled` to hide/show a whole section without touching route code —
 * this only affects sidebar visibility (matching "hide routes"), not access to the page
 * itself if someone navigates there directly.
 */
export type DashboardName = keyof typeof dashboardSections.dashboards;

export const getDashboardSections = () => dashboardSections.dashboards;

export function isRouteDashboardEnabled(routeKey: string): boolean {
  const dashboard = (dashboardSections.routes as Record<string, string>)[routeKey];
  if (!dashboard) return true; // not assigned to a dashboard — always visible
  return !!dashboardSections.dashboards[dashboard as DashboardName]?.enabled;
}

export const getSidebarRoutes = (role?: UserRole | string, cells?: PermissionCellLite[]): RouteConfig[] =>
  ALL.filter((route) =>
    !route.hideInSidebar &&
    route.type !== 'auth' &&
    !route.public &&
    routeVisibleFor(route, role, cells) &&
    isRouteDashboardEnabled(route.key));

export const getGroupedSidebarRoutes = (role?: UserRole | string, cells?: PermissionCellLite[]): Record<string, RouteConfig[]> => {
  const grouped: Record<string, RouteConfig[]> = {};
  for (const route of getSidebarRoutes(role, cells)) {
    const category = route.category ?? 'General';
    (grouped[category] ??= []).push(route);
  }
  return grouped;
};

export const publicRoutes = ALL.filter((route) => route.public);
export const authRoutes = ALL.filter((route) => route.type === 'auth');
export const protectedRoutes = ALL.filter((route) => !route.public && route.type !== 'auth');
export const adminRoutes = ALL.filter((route) => route.type === 'admin');
export const allRoutes = ALL;

/** @deprecated Alias for getGroupedSidebarRoutes — kept for admin-dashboard sidebar compat. */
export const getRoutesGroupedByCategory = getGroupedSidebarRoutes;
export const getRoutesForRole = getSidebarRoutes;
